import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Reusable full-screen image gallery lightbox. Used for viewing customer
 * ticket photos, employee proof-of-work photos, delivery proof photos,
 * marketplace product photos etc. — anywhere multiple images need a
 * fullscreen inspect view with keyboard / touch navigation.
 *
 * Props:
 *   images: [string]     - array of image src URLs (data URIs are fine)
 *   startIndex: number   - which image to open on (default 0)
 *   title: string        - shown top-left; helpful when the same lightbox
 *                          renders across multiple contexts
 *   onClose: () => void  - fires on X click, backdrop click, or Esc
 */
export default function ImageLightbox({ images = [], startIndex = 0, title = "", onClose }) {
  const [idx, setIdx] = useState(Math.max(0, Math.min(startIndex, images.length - 1)));

  const total = images.length;
  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);

  // Keyboard nav — Esc closes, left/right cycles. Registered once, freed on
  // unmount so multiple lightboxes stacked in different pages don't fight.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total, onClose]);

  if (!total) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      {/* Title + counter */}
      <div className="absolute top-3 left-4 right-16 text-white text-sm mono truncate pointer-events-none">
        {title && <span className="opacity-80">{title}</span>}
        {total > 1 && (
          <span className="opacity-60 ml-2">
            {idx + 1} / {total}
          </span>
        )}
      </div>

      {/* Close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-3 right-3 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
        aria-label="Close"
        data-testid="image-lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Prev / Next arrows (only if multiple images) */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
            aria-label="Previous"
            data-testid="image-lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
            aria-label="Next"
            data-testid="image-lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* The image itself — stop click propagation so tapping the image
          doesn't close the lightbox. */}
      <img
        src={images[idx]}
        alt={title || `Photo ${idx + 1}`}
        className="max-w-full max-h-full object-contain rounded"
        onClick={(e) => e.stopPropagation()}
        data-testid={`image-lightbox-img-${idx}`}
      />
    </div>
  );
}
